import { ActivityEntityName } from '../../../../entities/shared/activity.entity.name';
import { MetadataCatalog } from '../metadata.catalog';
import { MetadataEntityData, MetadataEntityVisualType } from '../model/metadata.entity.data';
import { MetadataEntityFieldData } from '../model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../model/metadata.entity.field.types';
import { getMetadataEntityFieldType } from './get.entity.field.type';

const FIELD_TYPE_ICONS: Partial<Record<MetadataEntityFieldType, string>> = {
    [MetadataEntityFieldType.String]: 'fa-regular fa-text',
    [MetadataEntityFieldType.StringAlphaNumeric]: 'fa-regular fa-text',
    [MetadataEntityFieldType.StringAlpha]: 'fa-regular fa-text',
    [MetadataEntityFieldType.Date]: 'fa-regular fa-calendar',
    [MetadataEntityFieldType.DateTime]: 'fa-regular fa-calendar-clock',
    [MetadataEntityFieldType.Time]: 'fa-regular fa-clock',
    [MetadataEntityFieldType.Duration]: 'fa-regular fa-stopwatch',
    [MetadataEntityFieldType.Number]: 'fa-regular fa-hashtag',
    [MetadataEntityFieldType.Percentage]: 'fa-regular fa-percent',
    [MetadataEntityFieldType.Currency]: 'fa-regular fa-dollar-sign',
    [MetadataEntityFieldType.Boolean]: 'fa-regular fa-toggle-on',
    [MetadataEntityFieldType.Flag]: 'fa-regular fa-flag',
    [MetadataEntityFieldType.Enum]: 'fa-regular fa-list',
    [MetadataEntityFieldType.Lookup]: 'fa-regular fa-magnifying-glass',
    [MetadataEntityFieldType.Parent]: 'fa-regular fa-link',
    [MetadataEntityFieldType.Tag]: 'fa-regular fa-tag',
    [MetadataEntityFieldType.EntityStatus]: 'fa-regular fa-circle-dot',
    [MetadataEntityFieldType.Email]: 'fa-regular fa-envelope',
    [MetadataEntityFieldType.Phone]: 'fa-regular fa-phone',
    [MetadataEntityFieldType.Url]: 'fa-regular fa-link',
    [MetadataEntityFieldType.Address]: 'fa-regular fa-location-dot',
    [MetadataEntityFieldType.Site]: 'fa-regular fa-location-dot',
    [MetadataEntityFieldType.Attachment]: 'fa-regular fa-paperclip',
    [MetadataEntityFieldType.Image]: 'fa-regular fa-image',
    [MetadataEntityFieldType.QR]: 'fa-regular fa-qrcode',
    [MetadataEntityFieldType.EmbeddedEntity]: 'fa-regular fa-cube',
    [MetadataEntityFieldType.Calculated]: 'fa-regular fa-calculator',
    [MetadataEntityFieldType.CustomFields]: 'fa-regular fa-sliders',
    [MetadataEntityFieldType.CustomField]: 'fa-regular fa-sliders',
};

/** Well-known entity visual types — convention-based defaults so decorators don't need to declare them */
const ENTITY_VISUAL_TYPES: Partial<Record<ActivityEntityName, MetadataEntityVisualType>> = {
    [ActivityEntityName.Contact]: 'avatar',
    [ActivityEntityName.ContactEmployee]: 'avatar',
    [ActivityEntityName.Customer]: 'avatar',
    [ActivityEntityName.Employee]: 'avatar',
    [ActivityEntityName.Resource]: 'avatar',
    [ActivityEntityName.Contractor]: 'avatar',
    [ActivityEntityName.Tag]: 'color',
    [ActivityEntityName.Priority]: 'color',
    [ActivityEntityName.JobType]: 'color',
    [ActivityEntityName.JobStatus]: 'status',
    [ActivityEntityName.WorkOrderStatus]: 'status',
    [ActivityEntityName.EntityStatus]: 'status',
    [ActivityEntityName.EntitySubStatus]: 'status',
};

const CUSTOM_FIELD_TYPE_ICONS: Record<string, string> = {
    em: 'fa-regular fa-envelope',
    ph: 'fa-regular fa-phone',
    txt: 'fa-regular fa-text',
    nt: 'fa-regular fa-text',
    num: 'fa-regular fa-hashtag',
    d: 'fa-regular fa-calendar',
    dt: 'fa-regular fa-calendar-clock',
    dd: 'fa-regular fa-list',
    ddm: 'fa-regular fa-list',
    bool: 'fa-regular fa-toggle-on',
    add: 'fa-regular fa-location-dot',
    img: 'fa-regular fa-image',
};

/**
 * Enriches metadata fields with resolved icon and visualType from target entities. Walks root fields
 * and the explicitly supplied secondaryFilter.metadata chain.
 */
export function enrichEntityFields(metadata: MetadataEntityData): MetadataEntityData {

    if (!metadata?.fields) return metadata;

    enrichFields(metadata.fields);

    let current: MetadataEntityData | undefined = metadata;
    const seen = new Set<string>();
    while (current?.secondaryFilter?.metadata?.fields) {
        const path = current.secondaryFilter.arrayPath;
        if (!path || seen.has(path)) break;
        seen.add(path);
        enrichFields(current.secondaryFilter.metadata.fields);
        current = current.secondaryFilter.metadata;
    }

    return metadata;
}

function enrichFields(fields: MetadataEntityFieldData[]): void {

    if (!fields) return;

    for (const field of fields) {

        const fieldType = getMetadataEntityFieldType(field);

        switch (fieldType) {
            case MetadataEntityFieldType.Lookup: {
                const entityName = typeof field.typeData === 'string' ? field.typeData : field.typeData?.type;
                if (entityName) {
                    const targetEntity = MetadataCatalog.getEntity(entityName);
                    field.icon = field.icon ?? targetEntity?.icon;
                    field.visualType = field.visualType ?? targetEntity?.visualType ?? ENTITY_VISUAL_TYPES[entityName as ActivityEntityName];
                }
                break;
            }

            case MetadataEntityFieldType.EmbeddedEntity: {
                const embeddedMeta = field.typeData as MetadataEntityData;
                if (embeddedMeta?.fields && !Array.isArray(field.type)) {
                    enrichFields(embeddedMeta.fields);
                }

                // Resolve from embedded entity's type if available
                if (embeddedMeta?.type) {
                    const targetEntity = MetadataCatalog.getEntity(embeddedMeta.type);
                    field.icon = field.icon ?? targetEntity?.icon;
                    field.visualType = field.visualType ?? targetEntity?.visualType ?? ENTITY_VISUAL_TYPES[embeddedMeta.type as ActivityEntityName];
                }
                break;
            }

            case MetadataEntityFieldType.CustomField: {
                const customType = field.typeData?.type;
                if (customType) {
                    field.icon = field.icon ?? CUSTOM_FIELD_TYPE_ICONS[customType];
                }
                break;
            }

            case MetadataEntityFieldType.Tag: {
                field.visualType = field.visualType ?? 'color';
                break;
            }

            case MetadataEntityFieldType.EntityStatus: {
                field.visualType = field.visualType ?? 'status';
                break;
            }
        }

        // Fall back to field type icon, then generic
        if (!field.icon) {
            field.icon = (fieldType ? FIELD_TYPE_ICONS[fieldType] : null) ?? 'fa-regular fa-circle';
        }
    }
}
